import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { FooterComponent } from '../footer/footer.component';
import { CompanyConfigService, CompanyConfig } from '../../services/company-config.service';
import { Router } from '@angular/router';

interface PreventionCategory {
  title: string;
  icon: string;
  description: string;
  tips: PreventionTip[];
}

interface PreventionTip {
  title: string;
  description: string;
  steps: string[];
  difficulty: 'Easy' | 'Moderate' | 'Advanced';
  season?: string;
  frequency?: string;
}

@Component({
  selector: 'app-prevention-tips',
  templateUrl: './prevention-tips.component.html',
  styleUrl: './prevention-tips.component.css',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTabsModule,
    MegamenuComponent,
    FooterComponent
  ]
})
export class PreventionTipsComponent {
  config: CompanyConfig;

  preventionCategories: PreventionCategory[] = [
    {
      title: 'General Home Protection',
      icon: 'home',
      description: 'Essential steps to protect your entire home from pest invasions',
      tips: [
        {
          title: 'Seal Entry Points',
          description: 'Prevent pests from entering your home by sealing common entry points',
          steps: [
            'Inspect foundation for cracks and seal with caulk',
            'Install door sweeps on exterior doors',
            'Repair or replace damaged window screens',
            'Seal gaps around pipes, cables, and utility lines',
            'Check and seal gaps around windows and doors'
          ],
          difficulty: 'Easy',
          season: 'Year-round',
          frequency: 'Quarterly inspection'
        },
        {
          title: 'Eliminate Moisture Sources',
          description: 'Remove excess moisture that attracts many pests',
          steps: [
            'Fix leaky pipes and faucets immediately',
            'Ensure proper ventilation in bathrooms and kitchens',
            'Clean gutters and ensure proper drainage',
            'Use dehumidifiers in damp areas like basements',
            'Remove standing water from plant saucers and containers'
          ],
          difficulty: 'Moderate',
          season: 'Year-round',
          frequency: 'Monthly check'
        },
        {
          title: 'Maintain Clean Environment',
          description: 'Keep your home clean to eliminate food sources for pests',
          steps: [
            'Vacuum regularly, especially in corners and under furniture',
            'Wipe down counters and clean up spills immediately',
            'Store food in airtight containers',
            'Take out garbage regularly and use tight-fitting lids',
            'Clean pet food areas and store pet food properly'
          ],
          difficulty: 'Easy',
          season: 'Year-round',
          frequency: 'Daily/Weekly'
        }
      ]
    },
    {
      title: 'Kitchen & Food Storage',
      icon: 'kitchen',
      description: 'Protect your kitchen and food storage areas from common pests',
      tips: [
        {
          title: 'Proper Food Storage',
          description: 'Store food correctly to prevent attracting pests',
          steps: [
            'Use airtight containers for dry goods like cereal, flour, and sugar',
            'Store fruits and vegetables in the refrigerator when possible',
            'Keep pet food in sealed containers',
            'Regularly check expiration dates and dispose of old food',
            'Clean pantry shelves regularly'
          ],
          difficulty: 'Easy',
          season: 'Year-round',
          frequency: 'Weekly'
        },
        {
          title: 'Kitchen Sanitation',
          description: 'Maintain a clean kitchen to eliminate pest attractants',
          steps: [
            'Clean up crumbs and spills immediately',
            'Wipe down appliances inside and out regularly',
            'Clean behind and under appliances monthly',
            'Empty and clean garbage cans weekly',
            'Keep sink and disposal clean and dry'
          ],
          difficulty: 'Easy',
          season: 'Year-round',
          frequency: 'Daily'
        }
      ]
    },
    {
      title: 'Outdoor Prevention',
      icon: 'yard',
      description: 'Prevent pests from establishing themselves around your property',
      tips: [
        {
          title: 'Landscape Management',
          description: 'Maintain your yard to reduce pest harborage areas',
          steps: [
            'Trim bushes and trees away from house exterior',
            'Remove leaf litter and debris from around foundation',
            'Keep grass cut short near the house',
            'Remove or relocate wood piles away from the house',
            'Eliminate standing water in planters and containers'
          ],
          difficulty: 'Moderate',
          season: 'Spring/Summer',
          frequency: 'Monthly'
        },
        {
          title: 'Exterior Maintenance',
          description: 'Keep the exterior of your home pest-resistant',
          steps: [
            'Repair damaged siding, roofing, or foundation',
            'Clean and maintain gutters regularly',
            'Ensure proper grading for water drainage',
            'Install or repair exterior lighting to reduce insect attraction',
            'Keep outdoor garbage cans clean and sealed'
          ],
          difficulty: 'Advanced',
          season: 'Spring/Fall',
          frequency: 'Seasonally'
        }
      ]
    },
    {
      title: 'Seasonal Prevention',
      icon: 'calendar_month',
      description: 'Season-specific prevention strategies for year-round protection',
      tips: [
        {
          title: 'Spring Preparation',
          description: 'Get your home ready for increased pest activity in spring',
          steps: [
            'Inspect and repair winter damage to exterior',
            'Clean up yard debris and trim vegetation',
            'Check and replace weatherstripping',
            'Service HVAC systems and replace filters',
            'Inspect attic and basement for signs of winter pests'
          ],
          difficulty: 'Moderate',
          season: 'Spring',
          frequency: 'Annually'
        },
        {
          title: 'Summer Vigilance',
          description: 'Maintain protection during peak pest season',
          steps: [
            'Monitor for increased ant and wasp activity',
            'Keep outdoor dining areas clean',
            'Maintain swimming pools and water features',
            'Check screens and repair any damage',
            'Be vigilant about food storage during outdoor activities'
          ],
          difficulty: 'Easy',
          season: 'Summer',
          frequency: 'Weekly'
        },
        {
          title: 'Fall Preparation',
          description: 'Prevent pests from overwintering in your home',
          steps: [
            'Seal cracks and gaps before cold weather',
            'Clean gutters and remove leaf debris',
            'Store outdoor furniture and equipment properly',
            'Inspect and seal around utility entries',
            'Remove outdoor food sources like fallen fruit'
          ],
          difficulty: 'Moderate',
          season: 'Fall',
          frequency: 'Annually'
        },
        {
          title: 'Winter Maintenance',
          description: 'Maintain pest prevention during winter months',
          steps: [
            'Monitor for rodent activity in storage areas',
            'Keep firewood stored away from the house',
            'Maintain proper humidity levels indoors',
            'Check for signs of overwintering pests in attics',
            'Keep kitchen and dining areas extra clean during holidays'
          ],
          difficulty: 'Easy',
          season: 'Winter',
          frequency: 'Monthly'
        }
      ]
    }
  ];

  quickTips = [
    {
      icon: 'lightbulb',
      tip: 'A clean home is your first line of defense against pests'
    },
    {
      icon: 'water_drop',
      tip: 'Eliminate moisture sources to reduce pest attraction'
    },
    {
      icon: 'lock',
      tip: 'Seal entry points smaller than a dime to keep pests out'
    },
    {
      icon: 'schedule',
      tip: 'Regular maintenance prevents small problems from becoming big ones'
    },
    {
      icon: 'eco',
      tip: 'Natural prevention methods are often the most effective'
    },
    {
      icon: 'visibility',
      tip: 'Early detection and action save time and money'
    }
  ];

  constructor(
    private companyConfig: CompanyConfigService,
    public router: Router
  ) {
    this.config = this.companyConfig.getConfig();
  }

  getDifficultyColor(difficulty: string): string {
    switch (difficulty) {
      case 'Easy': return '#4caf50';
      case 'Moderate': return '#ff9800';
      case 'Advanced': return '#f44336';
      default: return '#666';
    }
  }

  navigateToPestLibrary(): void {
    this.router.navigate(['/pest-library']);
  }

  navigateToFAQ(): void {
    this.router.navigate(['/resources/faqs']);
  }

  navigateToContact(): void {
    this.router.navigate(['/contact']);
  }



  callPhone() {
    this.companyConfig.callPhone();
  }

  sendEmail() {
    this.companyConfig.sendEmail();
  }
}